import type { NotificationReaderContext } from '@/modules/notifications/reads.api'
import { useEffect, useRef, useState } from 'react'
import { queryKeys } from '@/hooks/query-keys'
import { deliveryPreferencesApi } from '@/modules/notifications/delivery-preferences.api'
import { toast } from 'sonner'

import type { UpdateNotificationPreferenceDto } from '@scrambl/shared'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import { useNotificationPreferenceSession } from './useNotificationPreferenceSession'
import { useNotificationScopedQuery } from './useNotificationScopedQuery'
import { useOwnedReaderMutation } from './useOwnedReaderMutation'

type Choice = UpdateNotificationPreferenceDto & { revision: number }
type Draft = {
  key: string
  choices: Record<string, Choice>
  uncertain: boolean
  busy: boolean
}
type Command =
  | { kind: 'save'; preferences: UpdateNotificationPreferenceDto[] }
  | { kind: 'reset' }
const empty = (key: string): Draft => ({
  key,
  choices: {},
  uncertain: false,
  busy: false,
})
const pair = (p: UpdateNotificationPreferenceDto) => `${p.channel}-${p.type}`

/** Server bodies stay in the query cache; only unsent choices live in the form. */
export function useNotificationSettings() {
  const session = useNotificationPreferenceSession()
  const key = JSON.stringify(session.key)
  const [draft, setDraft] = useState<Draft>(() => empty(key))
  const current = draft.key === key ? draft : empty(key)
  const live = useRef(current)
  live.current = current
  const mounted = useRef(false)
  const flight = useRef<string | null>(null)
  const sequence = useRef(0)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const assertCurrent = () => {
    session.assertCurrent()
    if (!mounted.current) throw new SessionChangedError()
  }
  const isCurrent = () => {
    try {
      assertCurrent()
      return true
    } catch {
      return false
    }
  }
  const change = (update: (value: Draft) => Draft) => {
    assertCurrent()
    const next = update(live.current.key === key ? live.current : empty(key))
    live.current = next
    setDraft(next)
  }
  const reader = useNotificationScopedQuery(
    [...queryKeys.notifications.all, 'delivery-settings'],
    session,
    context => deliveryPreferencesApi.read(context, session.portalEntityId),
  )
  const command = useOwnedReaderMutation<
    Command,
    { updated: number },
    NotificationReaderContext
  >(session, {
    execute: (input, context) => {
      if (session.preview)
        throw new Error('Portal preview settings are read-only')
      return input.kind === 'reset'
        ? deliveryPreferencesApi.reset(context, session.portalEntityId)
        : deliveryPreferencesApi.save(
            { preferences: input.preferences },
            context,
            session.portalEntityId,
          )
    },
    queryRoot: queryKeys.notifications.all,
    singleFlight: true,
    errorMessage:
      'The notification change could not be confirmed. Refresh before trying again.',
  })
  const refreshReader = async () => {
    assertCurrent()
    const result = await reader.refetch({ throwOnError: true })
    assertCurrent()
    if (!Array.isArray(result.data))
      throw new Error('Settings reader unavailable')
  }
  const run = async (operation: () => Promise<void>) => {
    assertCurrent()
    if (flight.current === key)
      throw new Error('Settings action already in progress')
    flight.current = key
    change(value => ({ ...value, busy: true }))
    try {
      await operation()
    } finally {
      if (flight.current === key) flight.current = null
      if (isCurrent()) change(value => ({ ...value, busy: false }))
    }
  }
  const setChoices = (choices: UpdateNotificationPreferenceDto[]) => {
    assertCurrent()
    if (
      session.preview ||
      !reader.data ||
      reader.isError ||
      live.current.uncertain
    )
      return
    change(value => ({
      ...value,
      choices: {
        ...value.choices,
        ...Object.fromEntries(
          choices.map(choice => [
            pair(choice),
            { ...structuredClone(choice), revision: ++sequence.current },
          ]),
        ),
      },
    }))
  }
  const save = () =>
    run(async () => {
      if (
        session.preview ||
        !reader.data ||
        reader.isError ||
        live.current.uncertain
      )
        throw new Error('Refresh settings first')
      const submitted = Object.values(live.current.choices)
      if (!submitted.length) return
      let confirmed = false
      try {
        await command.mutateAsync({
          kind: 'save',
          preferences: submitted.map(({ revision, ...choice }) => choice),
        })
        assertCurrent()
        confirmed = true
        await refreshReader()
        change(value => ({
          ...value,
          choices: Object.fromEntries(
            Object.entries(value.choices).filter(
              ([name, choice]) =>
                !submitted.some(
                  sent =>
                    pair(sent) === name && sent.revision === choice.revision,
                ),
            ),
          ),
        }))
        toast.success('Notification preferences updated')
      } catch (error) {
        if (isCurrent()) {
          change(value => ({ ...value, uncertain: true }))
          if (confirmed)
            toast.error(
              'The change was confirmed, but settings could not be refreshed. Your choices are retained.',
            )
        }
        throw error
      }
    })
  const reset = () =>
    run(async () => {
      if (
        session.preview ||
        session.portalEntityId ||
        !reader.data ||
        reader.isError ||
        live.current.uncertain
      )
        throw new Error('Refresh settings first')
      const before = Object.values(live.current.choices)
      let confirmed = false
      try {
        await command.mutateAsync({ kind: 'reset' })
        assertCurrent()
        confirmed = true
        await refreshReader()
        change(value => ({
          ...value,
          choices: Object.fromEntries(
            Object.entries(value.choices).filter(
              ([name, choice]) =>
                !before.some(
                  sent =>
                    pair(sent) === name && sent.revision === choice.revision,
                ),
            ),
          ),
        }))
        toast.success('Default preferences restored')
      } catch (error) {
        if (isCurrent()) {
          change(value => ({ ...value, uncertain: true }))
          if (confirmed)
            toast.error(
              'Defaults were confirmed, but settings could not be refreshed. Your choices are retained.',
            )
        }
        throw error
      }
    })
  const refresh = () =>
    run(async () => {
      try {
        await refreshReader()
        change(value => ({ ...value, uncertain: false }))
      } catch (error) {
        if (isCurrent())
          toast.error('Notification settings could not be refreshed')
        throw error
      }
    })
  return {
    isPortal: session.portalEntityId !== null,
    readOnly: session.preview,
    preferences: reader.data ?? [],
    pendingChanges: Object.fromEntries(
      Object.entries(current.choices).map(([name, choice]) => [
        name,
        choice.enabled,
      ]),
    ),
    hasChanges: Object.keys(current.choices).length > 0,
    isLoading: session.enabled && reader.isPending,
    unavailable: !session.enabled || reader.isError || !reader.data,
    uncertain: current.uncertain,
    isSaving: current.busy,
    canRefresh: session.enabled && !current.busy,
    canSave:
      session.enabled &&
      !session.preview &&
      !!reader.data &&
      !reader.isError &&
      !current.uncertain &&
      !current.busy,
    setChoices,
    removeSmsChoices: () =>
      change(value => ({
        ...value,
        choices: Object.fromEntries(
          Object.entries(value.choices).filter(
            ([name]) => !name.startsWith('SMS-'),
          ),
        ),
      })),
    save,
    reset,
    refresh,
  }
}
